// src/App.js
import { useState, useEffect, useRef } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import axios from 'axios';

import Login from './Login';
import Register from './Register';
import Profile from './Profile';
import Admin from './Admin';
import './App.css';
import logo from './s2e.png';
import MoneyPlanner from './MoneyPlanner';

import heic2any from 'heic2any';
import { launchConfetti } from './utils/confetti';
import Dashboard from './Dashboard';
import BankConnectCard from './BankConnectCard';
import LandingPage from './LandingPage';

const API = 'https://snap2excel-backend.onrender.com';

/* ============================
   HEIC → JPEG HELPERS
   ============================ */

// Fallback using <canvas> if heic2any fails
async function canvasHeicToJpeg(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                return reject(new Error('Canvas toBlob returned null'));
              }
              const jpegFile = new File(
                [blob],
                file.name.replace(/\.heic$|\.heif$/i, '.jpg'),
                { type: 'image/jpeg' }
              );
              resolve(jpegFile);
            },
            'image/jpeg',
            0.9
          );
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = (err) => reject(err);
      img.src = reader.result; // data URL
    };

    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
}

async function convertHeicToJpeg(file) {
  // 1) Try heic2any first
  try {
    const blob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.9,
    });

    return new File(
      [blob],
      file.name.replace(/\.heic$|\.heif$/i, '.jpg'),
      { type: 'image/jpeg' }
    );
  } catch (err1) {
    console.warn('heic2any failed, trying canvas fallback', err1);

    // 2) Fallback to canvas conversion
    const jpegFromCanvas = await canvasHeicToJpeg(file);
    return jpegFromCanvas;
  }
}

/* =====================================
   BIG OVERVIEW FRONT PAGE COMPONENT
   ===================================== */
const OverviewTab = ({
  userName,
  isBusiness,
  totalReceipts,
  totalSpent,
  totalVat,
  incomePerMonth,
  billsPerMonth,
  spentThisMonth,
  weeklySavingTarget,
  leftThisMonth,
  yearlySavingsEstimate,
  onOpenDashboard,
}) => {
  const today = new Date();
  const weekday = today.toLocaleDateString('en-GB', { weekday: 'long' });
  const firstName = (userName || 'there').split(' ')[0];

  // simple rotating tips by day
  const tips = [
    'Small changes add up. Even £2 less per day is £730 per year.',
    'Scan receipts the same day – future you will thank you.',
    'Leftover money is your superpower. Protect it.',
    'Give every pound a job: bills, fun, and future you.',
    'Avoid “mystery spending” – Snap2Excel keeps the story clear.',
    'Treat savings like a bill you pay to yourself first.',
    'Side hustle? Track those receipts – that’s real profit.',
    'If it doesn’t bring joy or value, keep it out of your basket.',
    'Money is just a tool. You are the boss.',
    'Today is a good day to be kind to future you.',
  ];
  const tipIndex = today.getDate() % tips.length;
  const todayTip = tips[tipIndex];

  return (
    <div className="overview-root card-screen">
      {/* HERO – big greeting + tip */}
      <section className="overview-hero">
        <h1 className="overview-hero-title">
          Happy {weekday}, {firstName}.
        </h1>
        <p className="overview-hero-sub">
          Snap2Excel is watching your bills, receipts and leftover money for you.
        </p>

        <div className="overview-tip">
          <div className="overview-tip-label">Today’s tip 💡</div>
          <div className="overview-tip-text">{todayTip}</div>
        </div>
      </section>

      {/* MAIN GRID – left money + stock of the week */}
      <section className="overview-grid">
        {/* CARD 1 – Money left this month */}
        <div className="overview-card money-left-card">
          <div className="money-left-header">
            <span className="money-left-label">Money left this month</span>
            <span className="money-left-value">
              £{leftThisMonth.toFixed(2)}
            </span>
          </div>

          <div className="money-summary-grid">
            <div>
              <div className="money-summary-label">Income per month</div>
              <div className="money-summary-value">
                £{incomePerMonth.toFixed(2)}
              </div>
            </div>
            <div>
              <div className="money-summary-label">Bills per month</div>
              <div className="money-summary-value">
                £{billsPerMonth.toFixed(2)}
              </div>
            </div>
            <div>
              <div className="money-summary-label">Spent so far</div>
              <div className="money-summary-value">
                £{spentThisMonth.toFixed(2)}
              </div>
            </div>
            <div>
              <div className="money-summary-label">Weekly saving target</div>
              <div className="money-summary-value">
                £{weeklySavingTarget.toFixed(2)}
              </div>
            </div>
          </div>

          <p className="overview-money-note">
            Hit that weekly target and you’re on track for about{' '}
            <strong>£{yearlySavingsEstimate.toFixed(0)}</strong> saved in a year.
          </p>

          <button
            type="button"
            className="link-button"
            onClick={onOpenDashboard}
          >
            See full graphs on Dashboard →
          </button>
        </div>

        {/* CARD 2 – Stock of the week + why savings matter */}
        <div className="overview-card market-card">
          <h3 className="section-title">This week’s market spotlight</h3>
          <p className="section-subtitle">
            Every week we highlight one popular stock that’s getting a lot of
            attention in major finance news. This is{' '}
            <strong>information only</strong>, not investment advice.
          </p>

          <div className="market-highlight">
            <div className="market-name">Example pick: Apple (AAPL)</div>
            <div className="market-meta">
              Frequently mentioned by big media as a long-term tech company.
              Used here as a demo – future versions can update this automatically.
            </div>
            <div className="market-disclaimer">
              Your money, your choice. Always do your own research – Snap2Excel
              doesn’t recommend or guarantee any investment.
            </div>
          </div>

          <div className="market-savings-explainer">
            <h4>Why saving first matters</h4>
            <p>
              Before you think about investing, having steady savings makes you
              safe. A few hundred pounds kept aside each month builds a buffer
              for emergencies and gives you options later – whether that’s
              stocks, a business idea, or paying off debt faster.
            </p>
            <p>
              Snap2Excel’s job is to protect that leftover money and stop it
              leaking away on things you don’t really care about.
            </p>
          </div>
        </div>
      </section>

      {/* BOTTOM STRIP – totals above the scan bar */}
      <section className="overview-summary-strip">
        <div className="overview-summary-item">
          <span className="overview-summary-label">Total receipts</span>
          <span className="overview-summary-value">{totalReceipts}</span>
        </div>

        <div className="overview-summary-item">
          <span className="overview-summary-label">Total spent</span>
          <span className="overview-summary-value">
            £{totalSpent.toFixed(2)}
          </span>
        </div>

        {isBusiness && (
          <div className="overview-summary-item">
            <span className="overview-summary-label">Total VAT tracked</span>
            <span className="overview-summary-value">
              £{totalVat.toFixed(2)}
            </span>
          </div>
        )}
      </section>
    </div>
  );
};

// 🔹 Daily "best friend" money panel – now uses OverviewTab
function DailyMoneyPanel({
  token,
  refreshKey,
  stats,
  isBusiness,
  userName,
  onOpenDashboard,
}) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthlySpend, setMonthlySpend] = useState(0);

  useEffect(() => {
    if (!token) return;

    const authAxios = axios.create({
      baseURL: API,
      headers: { Authorization: `Bearer ${token}` },
    });

    const now = new Date();
    const currentMonthKey = `${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, '0')}`;

    const parseToMonthKey = (dateStr) => {
      if (!dateStr) return null;
      const m = String(dateStr).match(
        /(\d{1,4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,4})/
      );
      if (!m) return null;

      let a = parseInt(m[1], 10);
      let b = parseInt(m[2], 10);
      let c = parseInt(m[3], 10);

      let year, month;
      if (a > 1900) {
        year = a;
        month = b;
      } else if (c > 1900) {
        year = c;
        month = b;
      } else {
        year = c < 100 ? 2000 + c : c;
        month = b;
      }

      if (!month || month < 1 || month > 12) return null;
      return `${year}-${String(month).padStart(2, '0')}`;
    };

    const load = async () => {
      try {
        const [planRes, receiptsRes] = await Promise.all([
          authAxios.get('/api/money/plan'),
          authAxios.get('/api/receipts'),
        ]);

        setPlan(planRes.data);

        // how much spent this month
        const receipts = receiptsRes.data || [];
        let total = 0;
        receipts.forEach((r) => {
          let mk = parseToMonthKey(r.date);
          if (!mk) mk = currentMonthKey;
          if (mk !== currentMonthKey) return;
          const amt = Number(r.total) || 0;
          total += amt;
        });
        setMonthlySpend(total);
      } catch (err) {
        console.error('Daily panel money/plan error', err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token, refreshKey]);

  const now = new Date();
  const hour = now.getHours();
  let greeting = 'Hello';
  if (hour < 12) greeting = 'Good morning';
  else if (hour < 18) greeting = 'Good afternoon';
  else greeting = 'Good evening';

  const firstName = (userName || 'there').split(' ')[0];

  if (loading) {
    return (
      <div className="overview-root card-screen">
        <section className="dashboard-card daily-panel">
          <div className="daily-panel-header">
            <div className="daily-panel-greeting">
              Loading your money summary…
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!plan || !plan.hasData) {
    // user hasn’t set income/bills yet
    return (
      <div className="overview-root card-screen">
        <section className="dashboard-card daily-panel">
          <div className="daily-panel-header">
            <div className="daily-panel-greeting">
              {greeting}, {firstName} 👋
            </div>
            <span className="daily-panel-pill">Let’s get you set up</span>
          </div>
          <p className="daily-panel-text">
            Add your income and bills in the Money planner to unlock daily tips,
            predictions and a clear picture of what’s really left for you every
            month.
          </p>
          <p className="daily-panel-text">
            Tap <strong>“Money planner”</strong> at the top to start.
          </p>
        </section>
      </div>
    );
  }

  // numbers
  const monthlyIncome = Number(plan.monthlyIncome || 0);
  const monthlyBills = Number(plan.monthlyBills || 0);
  const leftoverBeforeSpend = Number(plan.leftover || 0);
  const spentThisMonth = Number(monthlySpend || 0);
  const leftoverAfterSpend = Math.max(0, leftoverBeforeSpend - spentThisMonth);

  const weeklyTarget = Number(plan.weeklyInvest || 0);
  const savingsBudget = Number(plan.savingsBudget || 0);
  const yearlyIfFollow = savingsBudget * 12;

  const totalReceipts = stats?.overall?.count || 0;
  const totalSpent = stats?.overall?.total || 0;
  const totalVat = stats?.overall?.vat || 0;

  // render the big overview hero + cards
  return (
    <OverviewTab
      userName={userName}
      isBusiness={isBusiness}
      totalReceipts={totalReceipts}
      totalSpent={totalSpent}
      totalVat={totalVat}
      incomePerMonth={monthlyIncome}
      billsPerMonth={monthlyBills}
      spentThisMonth={spentThisMonth}
      weeklySavingTarget={weeklyTarget}
      leftThisMonth={leftoverAfterSpend}
      yearlySavingsEstimate={yearlyIfFollow}
      onOpenDashboard={onOpenDashboard}
    />
  );
}


/* ============================
   MAIN RECEIPT APP
   ============================ */
function ReceiptApp({ token }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const [receipts, setReceipts] = useState([]);
  const [receiptsVersion, setReceiptsVersion] = useState(0); // 👈 NEW

  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [userPlan, setUserPlan] = useState('free');
  const [isAdmin, setIsAdmin] = useState(false);
  const [profile, setProfile] = useState(null);
const [usageMode, setUsageMode] = useState(null); // 'personal_budget' | 'self_employed' | 'business_owner'
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
const [isYouth, setIsYouth] = useState(false); // 👈 NEW
  const [moneyRefreshKey, setMoneyRefreshKey] = useState(0);
  const [activeCard, setActiveCard] = useState('overview'); // 'overview' | 'planner' | 'receipts'
  const touchStartX = useRef(null);
  const cardOrder = ['overview', 'planner', 'receipts', 'dashboard'];

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    shop: '',
    date: '',
    total: '',
    vat: '',
  });

  const [manualMode, setManualMode] = useState(false);
  const [manualForm, setManualForm] = useState({
    shop: '',
    date: '',
    total: '',
    vat: '',
    category: 'Other',
  });

   const handleTouchStart = (e) => {
    if (!e.touches || e.touches.length === 0) return;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    if (!e.changedTouches || e.changedTouches.length === 0) return;

    const startX = touchStartX.current;
    const endX = e.changedTouches[0].clientX;
    const diff = endX - startX;

    touchStartX.current = null;

    // small swipe? ignore
    if (Math.abs(diff) < 50) return;

    const idx = cardOrder.indexOf(activeCard);
    if (diff < 0 && idx < cardOrder.length - 1) {
      // swipe left → next card
      setActiveCard(cardOrder[idx + 1]);
    } else if (diff > 0 && idx > 0) {
      // swipe right → previous card
      setActiveCard(cardOrder[idx - 1]);
    }
  };
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const authAxios = axios.create({
    baseURL: API,
    headers: { Authorization: `Bearer ${token}` },
  });

  const fetchReceipts = async () => {
    try {
      const res = await authAxios.get('/api/receipts');
      setReceipts(res.data);
      setReceiptsVersion(v => v + 1);   // 👈 tell children “receipts changed”

    } catch (err) {
      console.error(err);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await authAxios.get('/api/dashboard');
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  };


const fetchProfile = async () => {
  try {
    const res = await authAxios.get('/api/profile');
    setProfile(res.data);
    setUserPlan(res.data.plan_type || 'free');
    setIsAdmin(!!res.data.is_admin);
    setUsageMode(res.data.usage_mode || null);

    // 👇 NEW: compute youth/adult from date_of_birth
    if (res.data.date_of_birth) {
      const dob = new Date(res.data.date_of_birth);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
        age--;
      }
      setIsYouth(age < 18);
    } else {
      setIsYouth(false);
    }
  } catch (err) {
    console.error('Error fetching profile', err);
  }
};


  useEffect(() => {
    fetchReceipts();
    fetchStats();
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

 const handleUpload = async (e) => {
  e.preventDefault();
  if (!file) return;

  setLoading(true);

  try {
    let uploadFile = file;

    const isHeic =
      (file.type && file.type.toLowerCase().includes('heic')) ||
      (file.type && file.type.toLowerCase().includes('heif')) ||
      file.name.toLowerCase().endsWith('.heic') ||
      file.name.toLowerCase().endsWith('.heif');

    if (isHeic) {
      try {
        uploadFile = await convertHeicToJpeg(file);
      } catch (convErr) {
        console.warn(
          'HEIC conversion failed, sending original file to backend',
          convErr
        );
        uploadFile = file;
      }
    }

    const fd = new FormData();
    fd.append('receipt', uploadFile, uploadFile.name);

    await authAxios.post('/api/scan-receipt', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    // 💥 FIRE CONFETTI ON SUCCESS
    launchConfetti();

    await fetchReceipts();
    await fetchStats();
    setMoneyRefreshKey((k) => k + 1);

    // reset states
    setFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  } catch (err) {
    console.error(err);
    const msg = err.response?.data?.error || 'Scan failed';
    alert(msg);
  } finally {
    setLoading(false);
  }
};


  const handleExport = () => {
    window.location.href = `${API}/api/export/csv?token=${token}`;
  };

  const handleLogout = () => {
  localStorage.removeItem('s2e_token');
  setTimeout(() => {
    window.location.href = '/login';
  }, 50);
};


  /* ======= Edit / Delete logic ======= */

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditForm({
      shop: r.shop || '',
      date: r.date || '',
      total:
        r.total === null || r.total === undefined ? '' : String(r.total),
      vat: r.vat === null || r.vat === undefined ? '' : String(r.vat),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ shop: '', date: '', total: '', vat: '' });
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveEdit = async () => {
    if (!editingId) return;

    const toNumber = (val) => {
      if (val === '' || val === null || val === undefined) return null;
      const n = Number(val);
      return Number.isNaN(n) ? null : n;
    };

    const payload = {
      shop: editForm.shop,
      date: editForm.date,
      total: toNumber(editForm.total),
      vat: toNumber(editForm.vat),
    };

    try {
      await authAxios.put(`/api/receipts/${editingId}`, payload);
      await fetchReceipts();
      await fetchStats();

            setMoneyRefreshKey((k) => k + 1);

      cancelEdit();
    } catch (err) {
      console.error('Edit save error', err);
      alert('Failed to save changes');
    }
  };

  const isFreePlan = userPlan === 'free';

  const deleteReceipt = async (id) => {
    if (!window.confirm('Delete this receipt?')) return;

    try {
      await authAxios.delete(`/api/receipts/${id}`);
      await fetchReceipts();
      await fetchStats();

            setMoneyRefreshKey((k) => k + 1);

    } catch (err) {
      console.error('Delete error', err);
      alert('Failed to delete receipt');
    }
  };

    const openReceiptImage = (path) => {
    if (!path) return;
    const url = `${API}/receipt-images/${path}`;
    setImagePreviewUrl(url);
  };

  /* ======= Dropzone helpers ======= */

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChosen = (f) => {
    if (!f) return;

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) handleFileChosen(f);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  /* ======= Manual receipt ======= */

  const saveManualReceipt = async () => {
    if (!manualForm.shop || !manualForm.total) {
      alert('Shop name and total are required.');
      return;
    }

    const payload = {
      shop: manualForm.shop,
      date: manualForm.date || null,
      total: Number(manualForm.total),
      vat: manualForm.vat ? Number(manualForm.vat) : 0,
      category: manualForm.category,
    };

     try {
    await authAxios.post('/api/receipts/manual', payload);

    // 🎉 Manual add success
    launchConfetti();

    await fetchReceipts();
    await fetchStats();
    setMoneyRefreshKey((k) => k + 1);

    setManualForm({
      shop: '',
      date: '',
      total: '',
      vat: '',
      category: 'Other',
    });
    setManualMode(false);
  } catch (err) {
    console.error('Manual add error:', err);
    alert('Failed to save manual receipt.');
  }

  };

  return (
    <div className="app-root">
      <div className="app-container">
        {/* HEADER */}
                {/* HEADER */}
        <header className="app-header">

  {/* LEFT —— Snap2Excel logo + plan */}
  <div className="header-left">
    <h1 className="app-title">
      <img src={logo} alt="logo" />
      Snap2Excel
      {userPlan !== 'free' && <span className="pro-badge">PRO</span>}
    </h1>

    <p className="app-subtitle">
      {(() => {
        if (usageMode === 'self_employed') {
          return 'Mode: Self-employed – keep every receipt ready for tax.';
        }
        if (usageMode === 'personal_budget') {
          return 'Mode: Personal budgeting – track your bills and leftover money.';
        }
        if (usageMode === 'business_owner') {
          return 'Mode: Business – central place for company receipts.';
        }
        if (userPlan === 'free') return 'Plan: Free (beta)';
        const isBusiness = profile?.account_type === 'business';
        if (isBusiness) return 'Plan: Business Pro (£1.49/week)';
        return 'Plan: Personal Pro (£0.99/week)';
      })()}
    </p>

    {userPlan === 'free' && (
      <>
        <button
          onClick={() =>
            (window.location.href =
              'https://buy.stripe.com/7sY5kEelv6Ad9kA91McQU00')
          }
          className="upgrade-btn"
        >
          Upgrade to Pro for £0.99/week
        </button>
        <p className="upgrade-caption">
          Unlock unlimited receipts, CSV/PDF exports, and full reports.
        </p>
      </>
    )}
  </div>

{/* RIGHT —— user profile */}
<div className="header-right">
  {profile ? (
    <div className="user-chip">
      <div className="user-avatar">
        {(profile.full_name || profile.email || 'U')
          .charAt(0)
          .toUpperCase()}
      </div>

      <div className="user-meta">
        <div className="user-name">
          {profile.full_name || profile.email}
        </div>
        <div className="user-type">
          {profile.account_type === 'business'
            ? 'Business Account'
            : 'Personal Account'}
        </div>
      </div>

      <button
        className="btn btn-secondary btn-xs"
        onClick={handleLogout}
      >
        Logout
      </button>
    </div>
  ) : (
    // Fallback when profile failed (e.g. token expired)
    <button
      className="btn btn-secondary btn-xs"
      onClick={handleLogout}
    >
      Login
    </button>
  )}
</div>

</header>


        {/* DASHBOARD TOTALS */}
                {/* 🔹 Top card tabs (Overview / Planner / Receipts) */}
                {/* 🔹 Top card tabs (Overview / Planner / Receipts + nav) */}
        <div className="card-tabs">
          <button
            type="button"
            className={activeCard === 'overview' ? 'card-tab active' : 'card-tab'}
            onClick={() => setActiveCard('overview')}
          >
            Overview
          </button>

          {profile?.account_type === 'personal' &&
            usageMode === 'personal_budget' && (
              <button
                type="button"
                className={activeCard === 'planner' ? 'card-tab active' : 'card-tab'}
                onClick={() => setActiveCard('planner')}
              >
                Money planner
              </button>
            )}

          <button
            type="button"
            className={activeCard === 'receipts' ? 'card-tab active' : 'card-tab'}
            onClick={() => setActiveCard('receipts')}
          >
            Receipts
          </button>

          {/* same style, navigation buttons */}
            <button
    type="button"
    className={activeCard === 'dashboard' ? 'card-tab active' : 'card-tab'}
    onClick={() => setActiveCard('dashboard')}
  >
    Dashboard
  </button>


          {isAdmin && (
            <button
              type="button"
              className="card-tab"
              onClick={() => navigate('/admin')}
            >
              Admin
            </button>
          )}

          <button
            type="button"
            className="card-tab"
            onClick={() => navigate('/profile')}
          >
            Profile
          </button>
        </div>


        {/* 🔹 Swipe area for cards */}
        <div
          className="cards-swipe-area"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {activeCard === 'overview' && (
  <div className="card-screen">
    <DailyMoneyPanel
      token={token}
      refreshKey={receiptsVersion}
      compact
      onOpenDashboard={() => navigate('/dashboard')}
      stats={stats}
      isBusiness={profile?.account_type === 'business'}
      userName={profile?.full_name || profile?.email}
    />
  </div>
)}
{activeCard === 'dashboard' && (
  <div className="card-screen">
    <Dashboard token={token} embedded />
  </div>
)}


          {/* === CARD 2: MONEY PLANNER === */}
          {activeCard === 'planner' && (
            <div className="card-screen">
              {profile?.account_type === 'personal' && usageMode === 'personal_budget' ? (
                <MoneyPlanner token={token} refreshKey={receiptsVersion} />
              ) : (
                <p style={{ padding: '1rem' }}>
                  Money planner is only available for personal budgeting accounts.
                </p>
              )}
            </div>
          )}

          {/* === CARD 3: RECEIPTS (Scan + Manual + List) === */}
          {activeCard === 'receipts' && (
            <div className="card-screen" id="receipts-section">
              {/* SCAN / EXPORT CARD (unchanged, just moved here) */}
              <section className="upload-card">
                <div className="upload-header">
                  <h2 className="section-title">Scan a new receipt</h2>
                  <p className="section-subtitle">
                    Drop a photo of any receipt here. We&apos;ll pull out totals &amp; VAT
                    and keep everything safely in Snap2Excel.
                  </p>
                </div>

                <form onSubmit={handleUpload} className="upload-form">
                  <div
                    className={`upload-dropzone ${
                      dragActive ? 'upload-dropzone-active' : ''
                    }`}
                    onClick={openFilePicker}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                  >
                    {previewUrl ? (
                      <div className="upload-preview">
                        <img src={previewUrl} alt="Receipt preview" />
                        <div className="upload-preview-text">
                          <div className="upload-preview-title">
                            1 file ready to scan
                          </div>
                          <div className="upload-preview-sub">
                            Click &quot;Scan receipt&quot; to process this photo, or
                            drop another one to replace it.
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="upload-dropzone-inner">
                        <div className="upload-circle-icon">📸</div>
                        <div>
                          <div className="upload-dropzone-title">
                            Drop a receipt photo here
                          </div>
                          <div className="upload-dropzone-sub">
                            or click to browse from your phone or computer
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <input
                    type="file"
                    accept="image/*,.heic,.heif"
                    ref={fileInputRef}
                    className="upload-input-hidden"
                    onChange={(e) => handleFileChosen(e.target.files[0])}
                  />

                  <div className="upload-actions-row">
                    <button
                      className="btn btn-primary upload-main-btn"
                      disabled={!file || loading}
                    >
                      {loading ? 'Processing…' : 'Scan receipt'}
                    </button>

                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setManualMode(!manualMode)}
                    >
                      + Add manually
                    </button>

                    <button
                      type="button"
                      className="btn btn-tertiary"
                      onClick={handleExport}
                      disabled={isFreePlan}
                      title={isFreePlan ? 'Pro feature – upgrade to unlock' : ''}
                    >
                      Export CSV
                    </button>
                    <button
                      type="button"
                      className="btn btn-tertiary"
                      onClick={() =>
                        (window.location.href = `${API}/api/report/monthly?token=${token}`)
                      }
                      disabled={isFreePlan}
                      title={isFreePlan ? 'Pro feature – upgrade to unlock' : ''}
                    >
                      Download Monthly PDF
                    </button>
                  </div>

                  {isFreePlan && (
                    <p className="upload-plan-note">
                      Free plan: you&apos;ve used <strong>{receipts.length}</strong> / 50
                      free receipts.
                      {receipts.length >= 40 && (
                        <> You&apos;re nearly at the limit – upgrade to keep scanning without interruptions.</>
                      )}
                    </p>
                  )}
                </form>
              </section>

              {/* MANUAL RECEIPT CARD (unchanged) */}
              {manualMode && (
                <div
                  className="manual-card"
                  style={{
                    marginTop: '14px',
                    padding: '16px',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    background: '#ffffff',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.05)',
                  }}
                >
                  <h3 className="section-title">Add receipt manually</h3>

                  <div
                    className="edit-strip-grid"
                    style={{ marginTop: '10px' }}
                  >
                    <input
                      className="auth-input"
                      placeholder="Shop"
                      value={manualForm.shop}
                      onChange={(e) =>
                        setManualForm({
                          ...manualForm,
                          shop: e.target.value,
                        })
                      }
                    />
                    <input
                      className="auth-input"
                      placeholder="Date (optional)"
                      value={manualForm.date}
                      onChange={(e) =>
                        setManualForm({
                          ...manualForm,
                          date: e.target.value,
                        })
                      }
                    />
                    <input
                      className="auth-input"
                      placeholder="Total (£)"
                      value={manualForm.total}
                      onChange={(e) =>
                        setManualForm({
                          ...manualForm,
                          total: e.target.value,
                        })
                      }
                    />
                    <input
                      className="auth-input"
                      placeholder="VAT (£)"
                      value={manualForm.vat}
                      onChange={(e) =>
                        setManualForm({
                          ...manualForm,
                          vat: e.target.value,
                        })
                      }
                    />
                  </div>

                  <select
                    className="auth-input"
                    style={{ marginTop: '10px' }}
                    value={manualForm.category}
                    onChange={(e) =>
                      setManualForm({
                        ...manualForm,
                        category: e.target.value,
                      })
                    }
                  >
                    <option value="Other">Other</option>
                    <option value="Food">Food</option>
                    <option value="Groceries">Groceries</option>
                    <option value="Fuel">Fuel</option>
                    <option value="Bills">Bills</option>
                    <option value="Shopping">Shopping</option>
                  </select>

                  <div
                    className="edit-strip-actions"
                    style={{ marginTop: '10px' }}
                  >
                    <button
                      className="btn btn-primary"
                      onClick={saveManualReceipt}
                    >
                      Save receipt
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setManualMode(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* EDIT STRIP (unchanged) */}
              {editingId && (
                <div className="edit-strip">
                  <p className="edit-strip-title">
                    Editing receipt #{editingId}
                  </p>
                  <div className="edit-strip-grid">
                    <input
                      className="auth-input"
                      placeholder="Shop"
                      value={editForm.shop}
                      onChange={(e) =>
                        handleEditChange('shop', e.target.value)
                      }
                    />
                    <input
                      className="auth-input"
                      placeholder="Date"
                      value={editForm.date}
                      onChange={(e) =>
                        handleEditChange('date', e.target.value)
                      }
                    />
                    <input
                      className="auth-input"
                      placeholder="Total"
                      value={editForm.total}
                      onChange={(e) =>
                        handleEditChange('total', e.target.value)
                      }
                    />
                    <input
                      className="auth-input"
                      placeholder="VAT"
                      value={editForm.vat}
                      onChange={(e) =>
                        handleEditChange('vat', e.target.value)
                      }
                    />
                  </div>
                  <div className="edit-strip-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={saveEdit}
                    >
                      Save changes
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={cancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* RECEIPTS LIST (unchanged) */}
              <section className="receipts-card">
                <div className="receipts-header">
                  <h2 className="section-title">All receipts</h2>
                  <p className="section-subtitle">
                    Every receipt you scan stays in one place. You can tidy up
                    totals, fix dates or delete anything you don&apos;t need.
                  </p>
                </div>
{receipts.length > 0 && (
  <div className="receipts-summary">
    <span>{receipts.length} receipt{receipts.length !== 1 ? 's' : ''}</span>
    {stats && (
      <span>£{stats.overall.total.toFixed(2)} total</span>
    )}
  </div>
)}

                <div className="receipt-list">
                  {receipts.length === 0 && (
                    <p className="receipt-empty">
                      No receipts yet. Scan your first one to see it here.
                    </p>
                  )}

                  {receipts.map((r) => (
                    <div key={r.id} className="receipt-row">
                      <div className="receipt-main">
                        <div className="receipt-title-line">
                          <span className="receipt-shop">
                            {r.shop || 'Unknown shop'}
                          </span>
                          <span className="receipt-total">
                            £{(r.total ?? 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="receipt-meta-line">
                          <span>{r.date || 'No date'}</span>
                          <span>VAT: £{(r.vat ?? 0).toFixed(2)}</span>
                          <span className="receipt-tag">
                            {r.category || 'Other'}
                          </span>
                        </div>
                      </div>
                      <div className="receipt-actions">
  {r.image_path && (
    <button
      type="button"
      className="icon-button"
      onClick={() => openReceiptImage(r.image_path)}
      title="View photo"
    >
      📷
    </button>
  )}
  <button
    type="button"
    className="icon-button"
    onClick={() => startEdit(r)}
    title="Edit"
  >
    ✏️
  </button>
  <button
    type="button"
    className="icon-button icon-button-danger"
    onClick={() => deleteReceipt(r.id)}
    title="Delete"
  >
    🗑
  </button>
</div>

                    </div>
                  ))}
                </div>
              </section>
{/* Image preview modal */}
{imagePreviewUrl && (
  <div
    className="image-modal-backdrop"
    onClick={() => setImagePreviewUrl(null)}
  >
    <div
      className="image-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <img
        src={imagePreviewUrl}
        alt="Receipt"
        style={{
          maxWidth: '90vw',
          maxHeight: '80vh',
          borderRadius: '12px',
        }}
      />
      <button
        className="btn btn-secondary"
        style={{ marginTop: '10px' }}
        onClick={() => setImagePreviewUrl(null)}
      >
        Close
      </button>
    </div>
  </div>
)}         

            </div>
          )}
        </div>

      
        {/* 🔹 Bottom dock – always visible */}
<div className="bottom-dock">
  <button
    type="button"
    className="dock-btn"
    onClick={() => {
      setActiveCard('receipts');
      setManualMode(true);
    }}
  >
    + Manual
  </button>

  <button
    type="button"
    className="dock-btn dock-btn-main"
    onClick={() => {
      setActiveCard('receipts');
      openFilePicker();
    }}
  >
    ● Scan
  </button>

  <button
    type="button"
    className="dock-btn"
    onClick={() => {
      setActiveCard('receipts');
      const el = document.getElementById('receipts-section');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }}
  >
    Receipts
  </button>
</div>

                        </div>
    </div>
  );
}

/* ============================
   APP ROUTER
   ============================ */
function App() {
  const [token, setToken] = useState(localStorage.getItem('s2e_token'));

  const handleLogin = async (email, password) => {
    try {
      const res = await axios.post(`${API}/api/login`, { email, password });
      const { token } = res.data;

      if (token) {
        setToken(token);
        localStorage.setItem('s2e_token', token);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Login error', err.response?.data || err);
      alert(err.response?.data?.error || 'Wrong email or password');
      return false;
    }
  };

  return (
    <Router>
      <Routes>
        {/* Public */}
        <Route path="/" element={<LandingPage onLogin={handleLogin} />} />
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route
          path="/register"
          element={<Register onRegister={handleLogin} />}
        />

        {/* Protected */}
        <Route
          path="/app"
          element={token ? <ReceiptApp token={token} /> : <Navigate to="/login" />}
        />
        <Route
          path="/dashboard"
          element={token ? <Dashboard token={token} /> : <Navigate to="/login" />}
        />
        <Route
          path="/profile"
          element={token ? <Profile token={token} /> : <Navigate to="/login" />}
        />
        <Route
          path="/admin"
          element={token ? <Admin token={token} /> : <Navigate to="/login" />}
        />

        {/* Fallback */}
        <Route
          path="*"
          element={token ? <Navigate to="/app" /> : <Navigate to="/" />}
        />
      </Routes>
    </Router>
  );
}



export default App;
